/* eslint-disable unicorn/prefer-ternary */
/* eslint-disable @typescript-eslint/ban-ts-comment */

import { camelCase, isArray } from 'lodash';

const toSnake = (key: string) => {
  return key?.replaceAll(/[A-Z]/g, (match) => `_${match.toLowerCase()}`);
};

const isRealObject = function (o: unknown) {
  return o === Object(o) && !isArray(o) && typeof o !== 'function';
};

interface Meta {
  /**
   * @description Keys for which the conversion should be skipped for direct descendants, they should be in camelCase
   */
  shallowIgnoreKeys?: string[];
  /**
   * @description Keys for which the conversion should be skipped for all descendants, they should be in camelCase
   */
  deepIgnoreKeys?: string[];
}

/**
 * An ignore key matches that key anywhere in the tree. Write it as a
 * dot-separated path (e.g. `additionalInfo.userInput`) to only match that
 * position, which matters when the same key name means different things in one
 * response. Array indices are not part of the path, so `a.b` matches `a[0].b`.
 */
const isIgnoredPath = (
  ignoreKeys: string[] | undefined,
  path: string
): boolean =>
  ignoreKeys?.some(
    (ignoreKey) => path === ignoreKey || path.endsWith(`.${ignoreKey}`)
  ) ?? false;

export const snakeToCamel = (
  input: unknown,
  { shallowIgnoreKeys, deepIgnoreKeys }: Meta = {},
  isIgnoreKey = false,
  path = ''
): unknown => {
  if (isRealObject(input)) {
    const inputRecord = input as Record<string, unknown>;
    const newObject: Record<string, unknown> = {};

    for (const key of Object.keys(inputRecord)) {
      const camelKey = camelCase(key);
      const keyPath = path ? `${path}.${camelKey}` : camelKey;
      const isShallowIgnoreKey = isIgnoredPath(shallowIgnoreKeys, keyPath);
      const isDeepIgnoreKey = isIgnoredPath(deepIgnoreKeys, keyPath);
      const transformedKey = isIgnoreKey ? key : camelKey;

      if (isShallowIgnoreKey) {
        newObject[transformedKey] = snakeToCamel(
          inputRecord[key],
          { shallowIgnoreKeys, deepIgnoreKeys },
          true,
          keyPath
        );
      } else if (
        inputRecord[key] instanceof Date ||
        camelKey === 'receiptFormConfig' ||
        camelKey === 'reimbursementFormConfig' ||
        camelKey === 'rjsfFormSchema' ||
        camelKey === 'rjsfFormData' ||
        camelKey === 'customData' ||
        camelKey === 'output' ||
        camelKey === 'expectedOutput' ||
        isDeepIgnoreKey
      ) {
        newObject[camelKey] = inputRecord[key];
      } else {
        const finalKey = isIgnoreKey ? key : camelKey;
        newObject[finalKey] = snakeToCamel(
          inputRecord[key],
          { shallowIgnoreKeys, deepIgnoreKeys },
          false,
          keyPath
        );
      }
    }

    return newObject;
  } else if (isArray(input)) {
    return input.map((item) => {
      return snakeToCamel(
        item,
        { shallowIgnoreKeys, deepIgnoreKeys },
        isIgnoreKey,
        path
      );
    });
  }
  return input;
};

/** Keys whose values contain user-defined arbitrary keys that must not be transformed. */
const SNAKE_PASSTHROUGH_KEYS = new Set(['expectedOutput', 'output']);

export const camelToSnake = function (object: unknown): unknown {
  if (isRealObject(object)) {
    const objectRecord = object as Record<string, unknown>;
    const newObject: Record<string, unknown> = {};

    for (const key of Object.keys(objectRecord)) {
      const snakeKey = toSnake(key);

      if (
        objectRecord[key] instanceof Date ||
        objectRecord[key] instanceof File ||
        SNAKE_PASSTHROUGH_KEYS.has(key)
      ) {
        newObject[snakeKey] = objectRecord[key];
      } else {
        newObject[snakeKey] = camelToSnake(objectRecord[key]);
      }
    }

    return newObject;
  } else if (isArray(object)) {
    return object.map((item) => {
      return camelToSnake(item);
    });
  }

  return object;
};
